// LaTeX Auto-fix — sends only the broken section of a LaTeX document
// to Claude, receives line-level replacements, and applies them surgically.
// Retries up to MAX_RETRIES times, restoring the original on total failure.

import { getStoredApiKey } from "./pdf-scanner";

const ANTHROPIC_API_URL = "https://api.anthropic.com/v1/messages";
const MODEL = "claude-haiku-4-5-20251001";

/** Number of context lines to include before/after the error region */
const CONTEXT_LINES = 40;

/** Maximum fix-compile-retry rounds */
const MAX_RETRIES = 5;

/**
 * A compile function that takes LaTeX source and either:
 * - resolves (compilation succeeded), or
 * - throws/rejects with an Error whose message contains the error + log
 */
export type CompileFn = (latex: string) => Promise<void>;

interface LineReplacement {
    line: number;       // 1-based line number (from the numbered snippet)
    old: string;        // exact original line content
    new: string | null; // replacement (null = delete the line)
}

/**
 * Extract a line number from a pdflatex error message.
 * pdflatex errors look like:  "l.42 \\some command"
 */
function extractErrorLine(errorMessage: string): number | null {
    const m = errorMessage.match(/l\.(\d+)/);
    return m ? parseInt(m[1], 10) : null;
}

/**
 * Extract the region of the document around the error,
 * returning numbered lines for the AI and the line range.
 */
function extractErrorRegion(
    source: string,
    errorMessage: string,
): { snippet: string; startLine: number; endLine: number } {
    const lines = source.split("\n");
    const errorLine = extractErrorLine(errorMessage);

    let center: number;
    if (errorLine && errorLine >= 1 && errorLine <= lines.length) {
        center = errorLine - 1; // 0-indexed
    } else {
        // No line number — send the whole document (capped at 200 lines around middle)
        center = Math.floor(lines.length / 2);
    }

    const start = Math.max(0, center - CONTEXT_LINES);
    const end = Math.min(lines.length, center + CONTEXT_LINES);
    const region = lines.slice(start, end);

    // Number each line so the AI can reference them precisely
    const numbered = region.map((l, i) => `${start + i + 1}: ${l}`).join("\n");

    return { snippet: numbered, startLine: start, endLine: end };
}

/**
 * Single-shot fix: ask Claude to fix one compilation error.
 * Returns the patched source or throws on failure.
 */
async function attemptSingleFix(
    latexSource: string,
    errorMessage: string,
    apiKey: string,
): Promise<string> {

    const { snippet, startLine } = extractErrorRegion(latexSource, errorMessage);

    const prompt = `You are a LaTeX expert. A pdflatex compilation failed with this error:

ERROR: ${errorMessage}

Here is the relevant section of the document (lines are numbered):

${snippet}

Return a JSON array of line replacements. Each object has:
- "line": the line number (as shown above)
- "old": the exact content of that line (after the "N: " prefix)
- "new": the fixed content (or null to delete the line)

Rules:
- Fix ALL errors you can see in this section, not just the first
- The "old" must match the line content EXACTLY
- Make minimal changes — only fix what causes compilation errors
- Common fixes: standalone \\\\ between paragraphs → delete (null); unclosed braces; broken \\caption
- Do NOT remove mathematical or textual content
- Return ONLY the raw JSON array, no markdown, no explanation

Example: [{"line":42,"old":"text here \\\\\\\\","new":"text here"}]`;

    const response = await fetch(ANTHROPIC_API_URL, {
        method: "POST",
        headers: {
            "x-api-key": apiKey,
            "anthropic-version": "2023-06-01",
            "anthropic-dangerous-direct-browser-access": "true",
            "content-type": "application/json",
        },
        body: JSON.stringify({
            model: MODEL,
            max_tokens: 2048,
            messages: [{ role: "user", content: prompt }],
        }),
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({})) as { error?: { message?: string } };
        if (response.status === 401) {
            throw new Error("Invalid API key. Please check your Anthropic API key and try again.");
        }
        if (response.status === 429) {
            throw new Error("Rate limit reached. Please wait a moment and try again.");
        }
        throw new Error(err.error?.message ?? `API error ${response.status}`);
    }

    const data = await response.json() as { content: Array<{ text: string }> };
    let raw = data.content[0].text.trim();

    // Strip markdown fences if present
    if (raw.startsWith("```")) {
        raw = raw.replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "");
    }

    let replacements: LineReplacement[];
    try {
        replacements = JSON.parse(raw) as LineReplacement[];
    } catch {
        throw new Error("Auto-fix returned an invalid response. Please try again.");
    }

    if (!Array.isArray(replacements) || replacements.length === 0) {
        throw new Error("Auto-fix could not identify any fixes for this error.");
    }

    // Apply replacements to the source lines
    const lines = latexSource.split("\n");
    let applied = 0;
    // Process in reverse line order so deletions don't shift subsequent line numbers
    const sorted = [...replacements]
        .filter((r) => typeof r.line === "number" && typeof r.old === "string")
        .sort((a, b) => b.line - a.line);

    for (const r of sorted) {
        const idx = r.line - 1; // convert to 0-indexed
        if (idx < 0 || idx >= lines.length) continue;

        // Verify the old content matches (trim to handle minor whitespace)
        if (lines[idx].trim() !== r.old.trim()) continue;

        if (r.new === null) {
            // Delete the line
            lines.splice(idx, 1);
        } else {
            lines[idx] = r.new;
        }
        applied++;
    }

    if (applied === 0) {
        throw new Error(
            `Auto-fix found ${replacements.length} fix(es) but none matched the source. ` +
            "The document may have changed since compilation. Try compiling again first."
        );
    }

    return lines.join("\n");
}

/**
 * Fix LaTeX compilation errors with up to MAX_RETRIES rounds of AI fixes.
 *
 * Flow per round:
 *   1. Send error region to AI → get line-level fixes
 *   2. Apply fixes to the source
 *   3. If `compileFn` is provided, recompile to verify
 *   4. If compilation fails again, loop with the new error
 *
 * If all rounds fail, the original document is preserved and an error is thrown.
 *
 * @param latexSource  The full LaTeX document that failed to compile
 * @param errorMessage The initial pdflatex error (including log context)
 * @param compileFn    Optional — compiles LaTeX and throws on failure
 */
export async function fixLatexError(
    latexSource: string,
    errorMessage: string,
    compileFn?: CompileFn,
): Promise<string> {
    const apiKey = getStoredApiKey();
    if (!apiKey) {
        throw new Error("No Anthropic API key configured. Please set your API key first.");
    }

    const original = latexSource;
    let current = latexSource;
    let currentError = errorMessage;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
            current = await attemptSingleFix(current, currentError, apiKey);
        } catch (err) {
            // AI fix itself failed (bad response, no matches, etc.)
            throw new Error(
                `Auto-fix failed after ${attempt} attempt(s): ` +
                (err instanceof Error ? err.message : String(err)) +
                "\n\nYour document has been restored to its original state."
            );
        }

        // If no compile function, return after single fix (caller handles compilation)
        if (!compileFn) return current;

        // Try compiling the fixed version
        try {
            await compileFn(current);
            // Compilation succeeded — we're done
            return current;
        } catch (compileErr) {
            currentError = compileErr instanceof Error ? compileErr.message : String(compileErr);
            // Continue to next attempt with the new error
            if (attempt < MAX_RETRIES) {
                console.log(`[auto-fix] Attempt ${attempt}/${MAX_RETRIES} still has errors, retrying...`);
            }
        }
    }

    // All retries exhausted — restore original
    throw new Error(
        `Auto-fix could not resolve all compilation errors after ${MAX_RETRIES} attempts. ` +
        "Your document has been restored to its original state."
    );
}
